// src/components/quotes/quoteAcceptance.js
//
// Pure helpers for the builder's Accepted panel. No React, no imports — kept
// separate so they can be unit-tested without dragging MSAL in behind
// QuoteBuilderPage, the same reason proposalStatus.js has no imports.

/**
 * Should the SPA move quote.status to "won" to reflect a client's acceptance?
 *
 * The acceptance is recorded in the view blob by a PUBLIC route, which
 * deliberately cannot write to the state blob. The SPA is the only writer of
 * `status`, so it closes the loop — once. `acceptanceReconciledAt` is the
 * stamp: without it a quote the client accepted and Craig then moved to "lost"
 * would be dragged back to "won" on every load, the app arguing with the
 * person using it. Mirrors proposals' needsWonReconciliation exactly.
 *
 * ⚠ Bookkeeping only. Craig's standing rule is untouched: acceptance MINTS
 * NOTHING — raising the invoice stays his explicit action.
 */
export function needsWonReconciliation(quote, activity) {
  if (!quote || !activity?.published) return false;
  if (!activity.acceptance?.accepted) return false;
  return quote.status !== "won" && !quote.acceptanceReconciledAt;
}

/** The patch the reconciliation writes. One place, so the stamp is never forgotten. */
export function wonReconciliationPatch(now = Date.now()) {
  return { status: "won", decidedAt: now, acceptanceReconciledAt: now };
}

/**
 * A timestamp as Craig reads it — Perth, whatever the browser's zone. Guards an
 * unparseable stamp: toLocaleString returns the LITERAL string "Invalid Date"
 * rather than throwing, so a try/catch never fires.
 */
export function whenPerth(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-AU", {
    dateStyle: "medium", timeStyle: "short", timeZone: "Australia/Perth",
  });
}

export function money(v) {
  return `$${Number(v || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

/**
 * Every accepted line in document order, each tagged with where it came from,
 * so a flat "order list" can be read straight off the summary.
 */
export function acceptedOrderLines(summary) {
  if (!summary) return [];
  const out = [];
  (summary.sections || []).forEach((s) => (s.lines || []).forEach((l) => out.push({
    ...l, sectionLabel: s.label || "", optionLabel: s.isChoice ? s.chosenLabel : "", hidden: !!s.hidden,
  })));
  (summary.commonLines || []).forEach((l) => out.push({
    ...l, sectionLabel: "Included in every option", optionLabel: "", hidden: false,
  }));
  return out;
}
